/**
 * The chantiers module's paths — spec §8.12.
 *
 * `/worksites` is what `layout/nav-items.ts` already points its `worksites` entry at,
 * gated on `worksites:read`, so the prefix is not this phase's to choose.
 *
 * ─── THE SITE DASHBOARD IS THE DETAIL ROUTE, NOT A TAB UNDER IT ─────────────
 *
 * A site manager arrives asking "who is on my chantier this morning", not "show me the
 * configuration record for chantier 4". So `/worksites/:worksiteId` IS the dashboard,
 * and the settings — client, dates, rate, working calendar — hang off `/settings`
 * beneath it. The reverse would put a form in front of the question everybody arrives
 * with.
 *
 * ─── THE STATEMENT CARRIES ITS PERIOD ON THE URL ────────────────────────────
 *
 * A billing statement without its period is a document nobody can check. `from` and
 * `to` are query parameters so a statement forwarded to an accountant reproduces
 * exactly the figures the sender saw — and so that reopening the link after a rebuild
 * of the daily log shows the difference rather than hiding it behind a default period
 * that has since moved.
 *
 * Route paths stay in English while the UI defaults to French: the address bar is not
 * a translated surface, and a localized set would mean two routing tables and a
 * redirect on every language switch.
 *
 * Registration lives in `routes/app-routes.tsx`, which this phase does not own.
 * NOTES-PHASE9-WEB-A.md §1 carries the patch, and it MUST lazy-load this module's
 * barrel: the site dashboard draws a map, and a static import would put ~1.45 MB of
 * WebGL renderer back into the entry chunk the map and trips splits exist to keep it
 * out of.
 */

export const WORKSITES_PATH = '/worksites'
export const WORKSITE_NEW_PATH = '/worksites/new'
export const WORKSITE_DETAIL_PATH = '/worksites/:worksiteId'
export const WORKSITE_SETTINGS_PATH = '/worksites/:worksiteId/settings'
export const WORKSITE_REPORT_PATH = '/worksites/:worksiteId/report'
export const WORKSITE_STATEMENT_PATH = '/worksites/:worksiteId/statement'
export const TRANSPORT_PATH = '/worksites/transport'

export const WORKSITE_QUERY_KEYS = {
  status: 'status',
  search: 'q',
  /** ISO-8601 instants. Both required on a billing document — see the header. */
  from: 'from',
  to: 'to',
  vehicleId: 'vehicle',
} as const

export function worksitePath(worksiteId: string): string {
  return `${WORKSITES_PATH}/${worksiteId}`
}

export function worksiteSettingsPath(worksiteId: string): string {
  return `${WORKSITES_PATH}/${worksiteId}/settings`
}

export function worksiteReportPath(worksiteId: string, period?: Period): string {
  return `${WORKSITES_PATH}/${worksiteId}/report${periodQuery(period)}`
}

export function worksiteStatementPath(
  worksiteId: string,
  period?: Period,
  vehicleId?: string,
): string {
  const query = new URLSearchParams()
  if (period !== undefined) {
    query.set(WORKSITE_QUERY_KEYS.from, period.from)
    query.set(WORKSITE_QUERY_KEYS.to, period.to)
  }
  if (vehicleId !== undefined) query.set(WORKSITE_QUERY_KEYS.vehicleId, vehicleId)
  const encoded = query.toString()
  return `${WORKSITES_PATH}/${worksiteId}/statement${encoded === '' ? '' : `?${encoded}`}`
}

export interface Period {
  readonly from: string
  readonly to: string
}

function periodQuery(period?: Period): string {
  if (period === undefined) return ''
  return `?${WORKSITE_QUERY_KEYS.from}=${encodeURIComponent(period.from)}&${WORKSITE_QUERY_KEYS.to}=${encodeURIComponent(period.to)}`
}

/**
 * The default period: the whole of last month, in UTC instants.
 *
 * Last month rather than "the last 30 days" because a rental invoice covers a month,
 * and a rolling window would produce a statement whose period nobody can reconcile
 * against a purchase order. The server SNAPS these bounds to whole local days anyway
 * (`machine_daily_log.day` is a calendar date in the tenant's zone) and prints the
 * snapped values on the document, so what the picker offers is a starting point rather
 * than the final claim.
 */
export function lastMonthPeriod(now: Date = new Date()): Period {
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
  return { from: start.toISOString(), to: end.toISOString() }
}
